import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiX,
  FiSave,
  FiBook,
  FiUsers,
  FiInfo,
  FiMapPin,
  FiLock
} from 'react-icons/fi';
import api from '../../../api';

const EditClassModal = ({
  isOpen,
  onClose,
  classId,
  className,
  tenantId,
  academicYears = [],
  semesters = [],
  departments = [],
  programs = [],
  notify,
  onClassUpdated
}) => {
  const [classDetails, setClassDetails] = useState(null);
  const [formData, setFormData] = useState({
    className: '',
    departmentIds: [],
    programId: '',
    maxStudents: 30 // <-- ADD THIS DEFAULT
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Memoize fetchClassDetails for stable reference in useEffect
  const fetchClassDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/classManager/class/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const cd = response.data.classData || response.data.class || response.data;
      setClassDetails(cd);

      const departmentIds = cd.departmentIds && Array.isArray(cd.departmentIds)
        ? cd.departmentIds
        : cd.departmentId
        ? [cd.departmentId]
        : [];

      setFormData({
        className: cd.className || '',
        departmentIds: departmentIds,
        programId: cd.programId?._id || cd.programId || '',
        maxStudents: cd.maxStudents || 30 // <-- ADD THIS
      });
    } catch (err) {
      console.error('Error fetching class details:', err);
      setError('Failed to load class details');
      notify?.('Failed to load class details', 'error');
    } finally {
      setLoading(false);
    }
  }, [classId, notify]);

  // ------------------ FETCH CLASS DETAILS ------------------ //
  useEffect(() => {
    if (isOpen && classId) {
      fetchClassDetails();
    }
  }, [isOpen, classId, fetchClassDetails]); // <-- Added fetchClassDetails

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setClassDetails(null);
      setFormData({
        className: '',
        departmentIds: [],
        programId: '',
        maxStudents: 30 // <-- RESET THIS ON CLOSE
      });
      setError(null);
    }
  }, [isOpen]);

  const getDepartmentDetails = useMemo(() => {
    if (!formData.departmentIds || formData.departmentIds.length === 0) return [];

    return formData.departmentIds.map((deptId, index) => {
      const deptIdStr = typeof deptId === 'object' ? deptId._id : deptId;
      const dept = departments.find(
        d => String(d._id) === String(deptIdStr) || String(d.id) === String(deptIdStr)
      );
      return {
        id: deptIdStr,
        name: dept?.name || dept?.departmentName || deptId?.name || 'Unknown Department',
        isPrimary: index === 0
      };
    });
  }, [formData.departmentIds, departments]);


  // ------------------ INPUT HANDLING ------------------ //

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Only allow className to be edited
    if (name !== 'className') {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ------------------ SUBMIT (UPDATE CLASS) ------------------ //

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.className.trim()) {
      notify?.('Class name is required', 'error');
      return;
    }
    if (!formData.maxStudents || formData.maxStudents < 1) {
      notify?.('Maximum students is required and must be a positive number', 'error');
      return;
    }

    // Check if there are any changes
    if (
      formData.className.trim() === classDetails?.className &&
      formData.maxStudents === (classDetails?.maxStudents || 30)
    ) {
      notify?.('No changes to save', 'info');
      onClose();
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(
        `/api/classManager/class/${classId}`,
        { 
          className: formData.className.trim(),
          maxStudents: formData.maxStudents // <-- ADD THIS
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      notify?.('Class updated successfully!', 'success');
      if (onClassUpdated) onClassUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating class:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update class';
      notify?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const currentProgram =
    programs.find(
      p => String(p._id) === String(formData.programId || classDetails?.programId)
    ) || null;

  // Helper to get subject count for display
  const getSubjectCount = () => {
    if (!classDetails) return 0;
    const subjects = classDetails.subjects || [];
    const labSubjects = classDetails.labSubjects || [];
    return subjects.length + labSubjects.length;
  };

  // Helper to get academic info for display
  const getAcademicInfo = () => {
    if (!classDetails) return 'N/A';
    
    const yearName = classDetails.academicYearId?.name || 
                     classDetails.academicYearId?.yearName || '';
    
    if (classDetails.isSemesterBased) {
      const semName = classDetails.semesterId?.semesterName || 
                      `Semester ${classDetails.semesterId?.semesterNumber || ''}`;
      return `${yearName} • ${semName}`;
    }
    
    return yearName || 'N/A';
  };

  return (
    /* z-[9999] layer forces this view to sit over all layout frames, side menus, and master headers */
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999] transition-all">
      
      {/* Mobile Bottom App Sheet Layout / Desktop Floating Modal Dialog */}
      <div className="bg-slate-100 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[95vh] overflow-hidden flex flex-col transform transition-transform border border-slate-200">
        
        {/* Mobile Swipe / Drag Indicator Visual */}
        <div className="w-12 h-1.5 bg-slate-300 rounded-full mx-auto my-2.5 block sm:hidden flex-shrink-0"></div>

        {/* Modal Top Header Panel */}
        <div className="bg-gradient-to-r from-slate-950 to-indigo-950 text-white p-5 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider">Edit Class Settings</h2>
              <p className="text-slate-300 text-xs font-medium mt-0.5">
                Update class name and seat limits below
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="w-8 h-8 flex items-center justify-center rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scroll Containment Body Section */}
        <div className="p-5 overflow-y-auto flex-1 space-y-5 bg-slate-100">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 space-y-2">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-indigo-600"></div>
              <span className="text-xs font-semibold text-slate-600">Loading class info...</span>
            </div>
          )}

          {error && !loading && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-center space-y-3">
              <p className="text-xs font-semibold text-rose-800 leading-relaxed">{error}</p>
              <button
                onClick={fetchClassDetails}
                className="px-4 py-2 bg-rose-600 text-white rounded-xl text-xs font-bold hover:bg-rose-700 transition-colors shadow-xs"
              >
                Try Again
              </button>
            </div>
          )}

          {classDetails && !loading && (
            <form onSubmit={handleSubmit} id="editClassForm" className="space-y-5">
              
              {/* Input Configuration Box */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs space-y-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                  <FiUsers className="text-indigo-600" size={14} />
                  Changeable Settings
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">
                      Class Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="className"
                      value={formData.className}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-indigo-600 focus:bg-white focus:border-indigo-600 transition-colors bg-slate-50"
                      placeholder="e.g., 3rd Sem BCA"
                      autoFocus
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">
                      Maximum Seats <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="maxStudents"
                      min={1}
                      value={formData.maxStudents}
                      onChange={e => setFormData(prev => ({ ...prev, maxStudents: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-indigo-600 focus:bg-white focus:border-indigo-600 transition-colors bg-slate-50"
                      placeholder="e.g., 60"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Immutable Mapping Context Box */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs space-y-3">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-1">
                  <FiLock className="text-slate-500" size={14} />
                  Fixed Settings (Cannot Change)
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-medium text-slate-700">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                    <span className="text-[10px] font-bold text-slate-500 uppercase block">Departments</span>
                    <div className="space-y-1 text-slate-900 font-bold">
                      {getDepartmentDetails.map((dept) => (
                        <div key={dept.id} className="flex items-center gap-1.5 truncate">
                          <FiMapPin className="text-slate-400 flex-shrink-0" size={12} />
                          <span className="truncate">
                            {dept.name} {dept.isPrimary && <span className="text-indigo-600 font-extrabold text-[10px]">(Main)</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1 flex flex-col justify-center">
                    <span className="text-[10px] font-bold text-slate-500 uppercase block">Program Course</span>
                    <span className="text-slate-900 font-bold truncate">
                      {currentProgram
                        ? `${currentProgram.name}${currentProgram.code ? ` (${currentProgram.code})` : ''}`
                        : 'Unknown Program'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Data Summary Analytics Grid */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs space-y-3">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-1">
                  <FiBook className="text-slate-500" size={14} />
                  Summary Details
                </h3>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
                    <div className="text-base font-black text-indigo-700">{getSubjectCount()}</div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Subjects</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
                    <div className="text-base font-black text-slate-800">{getDepartmentDetails.length}</div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Depts</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
                    <div className="text-base font-black text-emerald-700">
                      {classDetails.isSemesterBased ? 'Sem' : 'Year'}
                    </div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">System</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
                    <div className="text-base font-black text-amber-700">
                      {classDetails.facultyAssignments?.length || 0}
                    </div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Teachers</div>
                  </div>
                </div>

                {/* Academic Timeline Alert */}
                <div className="p-3 bg-indigo-50/60 text-indigo-950 border border-indigo-100 rounded-xl text-xs flex items-center gap-2 font-medium">
                  <FiInfo className="text-indigo-600 flex-shrink-0" size={14} />
                  <span>
                    <strong className="text-indigo-900 font-bold">Academic Block:</strong> {getAcademicInfo()}
                  </span>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Modal Sticky Bottom Actions Bar */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2.5 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 border border-slate-300 text-slate-700 font-bold text-xs uppercase tracking-wider rounded-xl transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="editClassForm"
            disabled={saving || !classDetails || !formData.className.trim()}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-colors disabled:opacity-50 flex items-center gap-1.5 shadow-sm shadow-indigo-600/10"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <FiSave size={13} />
                Save Changes
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditClassModal;